"use client";

import clsx from "clsx";
import { useTranslations } from "next-intl";
import { advanceScreen } from "@/lib/presenter/advanceScreen";

/**
 * Screen 2.4b — T1-only handshake beat between the invoice issuance (2.4)
 * and the SmartLogistics screen (2.5). Two-pane split-screen showing:
 *
 *   LEFT (importer view — Al Madar):
 *     "Programmable escrow topped up" — Al Madar funds the digital escrow
 *     account from buyer working capital. Confirms the trade is funded.
 *
 *   RIGHT (exporter view — Aurora):
 *     "Escrow funded — release goods" — Aurora receives the funded
 *     notification and the release CTA.
 *
 * This screen is T1-only (Programmable Escrow). T2/T3 use the LC + bank
 * gate cascade instead, so they skip this handshake.
 */
export function EscrowFundedHandshake({ className }: { className?: string }) {
  const t = useTranslations("escrowFunded");
  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass relative overflow-hidden p-4">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-state-financed to-transparent" />
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
          {t("kicker")}
        </p>
        <h2 className="mt-1 font-display text-xl font-semibold text-orbitra-paper">
          {t("handshakeTitle")}
        </h2>
        <p className="mt-1 text-[12px] text-orbitra-teal-soft">
          {t("handshakeSubtitle")}
        </p>
      </div>

      <div className="grid flex-1 min-h-0 grid-cols-2 gap-4">
        {/* Importer (Al Madar) view */}
        <div className="glass flex min-h-0 flex-col p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
            {t("importerView")}
          </p>
          <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">
            {t("importerTitle")}
          </h3>
          <p className="mt-2 text-[12px] text-orbitra-teal-soft">
            {t("importerSubtitle")}
          </p>

          <div className="mt-4 rounded-md border border-state-verified/30 bg-orbitra-ink/40 p-4">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-muted">{t("escrowAccount")}</span>
              <span className="font-mono text-[11px] text-orbitra-paper">ORB-ESC-T1-0042</span>
            </div>
            <div className="mt-2 flex items-baseline justify-between gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-muted">{t("holderLabel")}</span>
              <span className="text-[11px] text-orbitra-paper">{t("holderT1")}</span>
            </div>
            <div className="mt-2 flex items-baseline justify-between gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-muted">{t("topUpAmount")}</span>
              <span className="font-display text-base font-semibold text-state-financed">USD 850,000</span>
            </div>
            <div className="mt-2 flex items-baseline justify-between gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-muted">{t("sourceLabel")}</span>
              <span className="text-[11px] text-orbitra-paper">{t("sourceValue")}</span>
            </div>
            <div className="mt-2 flex items-baseline justify-between gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-muted">{t("statusLabel")}</span>
              <span className="rounded-full border border-policy-pass/50 bg-policy-pass/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-policy-pass">
                {t("toppedUpChip")}
              </span>
            </div>
          </div>

          <p className="mt-3 text-[10px] text-orbitra-muted">
            {t("fundsLockedNote")}
          </p>
        </div>

        {/* Exporter (Aurora) view */}
        <div className="glass flex min-h-0 flex-col p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">
            {t("exporterView")}
          </p>
          <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">
            {t("exporterTitle")}
          </h3>
          <p className="mt-2 text-[12px] text-orbitra-teal-soft">
            {t("exporterSubtitle")}
          </p>

          <div className="mt-4 rounded-md border border-state-traced/30 bg-state-traced/8 p-4">
            <div className="flex items-center gap-2">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-state-traced/20 text-[11px] font-bold text-state-traced">
                ✉
              </span>
              <div className="flex flex-col">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">{t("inboxJustNow")}</span>
                <span className="font-mono text-[10px] text-orbitra-muted">{t("traderEmail")}</span>
              </div>
            </div>
            <p className="mt-3 text-[13px] font-semibold text-orbitra-paper">
              {t("notificationHeadline")}
            </p>
            <p className="mt-2 text-[11px] text-orbitra-teal-soft italic">
              {t("notificationBody")}
            </p>
            <p className="mt-3 font-mono text-[9px] text-orbitra-muted">
              {t("refFooter")}
            </p>
          </div>

          <button
            type="button"
            onClick={advanceScreen}
            className="cta-primary mt-auto w-full py-2.5 text-[12px] font-semibold"
          >
            {t("acknowledgeCta")}
          </button>
        </div>
      </div>
    </div>
  );
}
